import LegalLayout from "./LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" effectiveDate="February 13, 2026">
      <p><strong>Provider:</strong> SALESARENA HQ LLC.<br />
      <strong>Contact:</strong> <a href="mailto:support@salesarenahq.com">support@salesarenahq.com</a></p>

      <p>This Privacy Policy explains what personal data SalesArena HQ collects, how we use it, who we share it with, and your choices. This policy applies to the SalesArena HQ web application and related services (the "Service").</p>

      <p><em>This policy is drafted in plain language and has not been reviewed by an attorney. Consult legal counsel before relying on it for compliance with GDPR, CCPA, HIPAA, or other regimes.</em></p>

      <h2>1. Data We Collect</h2>
      <h3>Account &amp; Profile</h3>
      <ul>
        <li>Name, email address, hashed password (bcrypt), role within your Company</li>
        <li>Optional: avatar image, personal accent color, biographical text</li>
        <li>Login timestamps, session tokens (secure HTTP-only cookies)</li>
      </ul>
      <h3>Sales &amp; Gamification Data</h3>
      <ul>
        <li>Sales entries you or your team log (lines, segment, optional notes)</li>
        <li>Points, streaks, badges unlocked, cosmetic frames earned, level and tier progression</li>
        <li>Duel and team-war participation, wins/losses, activity feed entries</li>
      </ul>
      <h3>Content You Upload</h3>
      <ul>
        <li>Announcement text, image and GIF attachments (auto-compressed and size-limited), reactions, comments</li>
        <li>Custom badge and challenge definitions created by your Owner</li>
      </ul>
      <h3>Technical Data</h3>
      <ul>
        <li>IP address, browser type, device information, error logs (used for security and debugging)</li>
        <li>Stripe payment tokens (we do <strong>not</strong> store full card numbers — Stripe holds those directly)</li>
      </ul>

      <h2>2. How We Use Data</h2>
      <ul>
        <li>To operate the Service: authenticate you, display leaderboards, award points, run challenges</li>
        <li>To provide customer support (Owner-invited support sessions may include impersonation — see §5)</li>
        <li>To process payments and manage subscriptions (via Stripe)</li>
        <li>To send transactional emails (invites, password resets, weekly digests — via Resend)</li>
        <li>To improve the Service (aggregated, anonymized analytics)</li>
        <li>To comply with law and enforce our Terms</li>
      </ul>
      <p>We do <strong>not</strong> sell your personal data. We do not use your data to train AI models.</p>

      <h2>3. Data Sharing &amp; Sub-processors</h2>
      <p>We share limited data with the following sub-processors strictly to operate the Service:</p>
      <ul>
        <li><strong>Stripe</strong> — payment processing (US) — <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a></li>
        <li><strong>Resend</strong> — transactional email delivery (US) — <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noreferrer">resend.com/legal/privacy-policy</a></li>
        <li><strong>Emergent Object Storage</strong> — image/attachment hosting</li>
        <li><strong>Google (OAuth)</strong> — optional sign-in via your Google account</li>
        <li><strong>SARA Plus</strong> — optional dealer-sales sync (only for Premium Companies who opt in and provide credentials)</li>
      </ul>
      <p>We do not share data with advertisers or data brokers.</p>

      <h2>4. Cross-Company Isolation</h2>
      <p>Every Company account is logically isolated at the database and API layer. Users in Company A cannot see any data belonging to Company B. Cross-company challenges are opt-in and only expose team names, member names, and match scores — not the underlying sales entries or client details.</p>

      <h2>5. Support Access &amp; Impersonation</h2>
      <p>To resolve support tickets, SalesArena HQ platform administrators may occasionally "act as" a Company Owner to reproduce the reported issue. Every impersonation session is:</p>
      <ul>
        <li>Restricted to platform-admin users (not your Company admins)</li>
        <li>Logged in your Company Activity Feed as <code>impersonation_start</code> / <code>impersonation_end</code></li>
        <li>Read-only where possible; write actions taken during impersonation are attributed to the platform admin in the audit trail</li>
      </ul>
      <p>If you wish to opt out of impersonation access, email <a href="mailto:support@salesarenahq.com">support@salesarenahq.com</a>. Note that this may limit our ability to reproduce and fix bugs on your behalf.</p>

      <h2>6. Data Retention</h2>
      <p>We retain data while your subscription is active and for 30 days after cancellation to allow reactivation. After 30 days, personal data is permanently deleted from primary systems; encrypted backups are purged on a rolling 90-day cycle. Sales entries older than 3 years may be archived to cold storage but remain accessible on request.</p>

      <h2>7. Your Rights</h2>
      <p>Depending on your jurisdiction, you may have rights to:</p>
      <ul>
        <li>Access the personal data we hold about you</li>
        <li>Correct inaccurate data</li>
        <li>Delete your data ("right to be forgotten")</li>
        <li>Export your data in a portable format</li>
        <li>Object to certain processing</li>
      </ul>
      <p>To exercise any of these rights, email <a href="mailto:support@salesarenahq.com">support@salesarenahq.com</a> from the address on file. We will respond within 30 days.</p>

      <h2>8. Security</h2>
      <p>We use industry-standard practices: HTTPS for all traffic, bcrypt for password hashing, HTTP-only secure cookies for sessions, chunked-upload guardrails to prevent oversized uploads from exhausting memory, and role-based access control at the API layer. No system is 100% secure — please report vulnerabilities to <a href="mailto:support@salesarenahq.com">support@salesarenahq.com</a>.</p>

      <h2>9. Children</h2>
      <p>The Service is not directed at children under 16. We do not knowingly collect data from children. If you believe a child has provided data, contact us to have it removed.</p>

      <h2>10. International Users</h2>
      <p>Our servers are located in the United States. By using the Service outside the U.S., you consent to the transfer of your data to the U.S. for processing.</p>

      <h2>11. Changes</h2>
      <p>Material changes to this policy will be announced by email to Company Owners and via an in-app banner at least 30 days before taking effect.</p>

      <h2>12. Contact</h2>
      <p>Questions or requests: <a href="mailto:support@salesarenahq.com">support@salesarenahq.com</a>.</p>
    </LegalLayout>
  );
}
